/**
 * Header class manages the global navigation header, including responsive behavior,
 * dropdowns, search, and skinning based on page sections.
 *
 * @class Header
 */
import setCssVar from "../utils/setCssVar";

export default class Header {
  /**
   * @param {HTMLElement} elem - The header DOM element
   * @param {Object} APP - The global app object for state sharing
   */
  constructor(elem, APP) {
    this.APP = APP;
    this.APP.header = {
      state: null,
      height: null,
      setSkin: (skin) => (this.elem.dataset.skin = skin),
    };

    this.elem = elem;
    this.headerHeight = this.elem.offsetHeight;
    // document.body.style.setProperty("--header-height", `${this.headerHeight}px`);
    // this.collapsedHeight = 60;
    this.sections = document.querySelectorAll("main > section") || [];

    // DOM references
    this.logo = this.elem.querySelector(".header__logo-link");
    this.primaryWrap = this.elem.querySelector(".header__primary-wrap");
    this.primaryNav = this.elem.querySelector(".header__primary-nav");
    this.utilityWrap = this.elem.querySelector(".header__utility-wrap");
    this.utilityWrapHeight = this.utilityWrap ? this.utilityWrap.offsetHeight : 0;
    this.utilityLinks = this.elem.querySelector(".header__utility-links");
    this.searchButton = this.elem.querySelector(".header__search-button");
    this.searchWrap = this.elem.querySelector(".header__search-wrap");
    this.searchCloseButton = this.elem.querySelector(".header__search-close-button");
    this.primaryNavListItems = this.elem.querySelectorAll('.header__primary-nested-item') ?? null;
    this.rightContentWrap = this.elem.querySelector('.right-content-wrap');
    this.defaultRightContent = this.elem.querySelectorAll('.default-right-content');
    this.sectionRightContents = this.elem.querySelectorAll('.section-right-content');
    this.subCategoryButtons = this.elem.querySelectorAll('[data-menu-item-type="section"] > .header__secondary-link');
    this.mobileRightContentWrap = this.elem.querySelectorAll('div:not(.accordion-content-wrap) > .mobile-menu.right-content-wrap') ?? null;
    this.hrefLinks = this.elem.querySelectorAll('.header__primary-nav a[href]') ?? null;
    this.mobileNav = this.elem.querySelector('.mobile-menu');

    // Class names used throughout the header
    this.CLASS_NAMES = {
      PARENT_BUTTON: "js-header-modal-parent-button",
      NESTED_MENU: "js-header-modal-nested-menu",
      NESTED_MENU_BACK: "js-header-modal-nested-menu-back",
      EXPANDED_SUB_MENU: "header__modal-nested-menu--is-shown",
      DESKTOP_COLLAPSED_UTILITY_DROPDOWN: "header__utility-dropdown--is-collapsed",
      DESKTOP_UTILITY_DROPDOWN: "js-header-utility-dropdown",
      DESKTOP_PRIMARY_DROPDOWN: "js-header-primary-dropdown",
      DESKTOP_ACTIVE_PRIMARY_BUTTON: "header__primary-link--active",
      DESKTOP_PRIMARY_NESTED_GRID: "js-header-primary-dropdown-container",
      DESKTOP_EXPANDED_PRIMARY_DROPDOWN: "header__primary-dropdown--is-expanded",
      NORMAL: "header--normal",
      ANIMATE: "header--animate",
      EXPANDED: "header--expanded",
      COLLAPSED: "header--collapsed",
    };

    // Key DOM elements for toggling state
    this.ELEMENTS = {
      MOBILE_BUTTON_OPEN_NAV: this.elem.querySelector(".js-open-nav"),
      MOBILE_BUTTON_CLOSE_NAV: this.elem.querySelector(".js-close-nav"),
      MOBILE_MODAL: this.elem.querySelector(".js-header-modal"),
      DESKTOP_UTILITY_PARENT_BUTTONS: this.elem.querySelectorAll(".js-header-utility-parent-button"),
      DESKTOP_UTILITY_LINKS: this.elem.querySelector(".js-header-utility-links"),
      DESKTOP_UTILITY_DROPDOWNS: this.elem.querySelectorAll(`.${this.CLASS_NAMES.DESKTOP_UTILITY_DROPDOWN}`),
      DESKTOP_ACTIVE_UTILITY_DROPDOWN: null,
      DESKTOP_PRIMARY_PARENT_BUTTONS: this.elem.querySelectorAll(".js-header-primary-parent-button"),
      DESKTOP_ACTIVE_PRIMARY_DROPDOWN: null,
      DESKTOP_PRIMARY_DROPDOWNS: this.elem.querySelectorAll(`.${this.CLASS_NAMES.DESKTOP_PRIMARY_DROPDOWN}`),
      DESKTOP_PRIMARY_NESTED_GRIDS: this.elem.querySelectorAll(`.${this.CLASS_NAMES.DESKTOP_PRIMARY_NESTED_GRID}`),
      DESKTOP_ACTIVE_PRIMARY_BUTTON: null,
      DESKTOP_PRIMARY_WRAP: this.elem.querySelector(".js-header-primary-wrap"),
    };

    // State flags
    this.FLAGS = {
      IS_MODAL_OPEN: false,
      IS_DESKTOP_UTILITY_DROPDOWN_OPEN: false,
      IS_DESKTOP_PRIMARY_DROPDOWN_OPEN: false,
      IS_INITIAL_SCROLL_EVENT: true,
    };

    // Device and scroll state
    this.deviceType = null;
    this.previousDeviceType = null;
    this.previousScrollTop = null;
    this.scrollBuffer = 30;
    this.dropdownHeightBuffer = 60;
    this.matchedMedia = {
      'isSmMobile': false,
      'isMobile': false,
      'isDesktop': false,
    };

    // For outside click handling on desktop subnav
    this._desktopPrimaryOutsideClickHandler = null;
    // For outside click handling on desktop utility nav
    this._desktopUtilityOutsideClickHandler = null;
  }

  /**
   * Handles scroll events to update header state (collapsed/expanded/normal).
   */
  onScroll() {
    const scrollPos = window.scrollY;
    const isScrollingDown = scrollPos > this.previousScrollTop;
    const isScrollingUp = scrollPos < this.previousScrollTop;
    const isScrollPosAfterNavBar = scrollPos > this.scrollBuffer;
    const isScrollPosAtTop = scrollPos <= 0;
    const isHeaderNormal = this.elem.classList.contains(this.CLASS_NAMES.NORMAL);
    const primaryWrapHeight = this.ELEMENTS.DESKTOP_PRIMARY_WRAP.offsetHeight;

    // console.table({
    //   scrollPos,
    //   isScrollingDown,
    //   isScrollingUp,
    //   isScrollPosAfterNavBar,
    //   isScrollPosAtTop,
    // });

    // Initial scroll event setup
    if (this.FLAGS.IS_INITIAL_SCROLL_EVENT) {
      this.previousScrollTop = scrollPos;
      setCssVar(null, '--header-height-dynamic', `${primaryWrapHeight}px`);
      if (isScrollPosAfterNavBar) {
        this.elem.classList.add(this.CLASS_NAMES.ANIMATE, this.CLASS_NAMES.EXPANDED);
        this.elem.classList.remove(this.CLASS_NAMES.NORMAL);
        setCssVar(null, '--header-height-dynamic', `${this.elem.offsetHeight}px`);
      }
      this.FLAGS.IS_INITIAL_SCROLL_EVENT = false;
      return;
    }
    if (this.previousScrollTop === null) return;

    // Collapse header on scroll down
    if (isScrollingDown && isScrollPosAfterNavBar) {
      if (Math.abs(this.previousScrollTop - scrollPos) <= this.scrollBuffer) return;
      // setCssVar(null, '--header-height-dynamic', `${primaryWrapHeight}px`);
      this.APP.header.state = 'collapsed';
      this.APP.header.height = primaryWrapHeight;
      if (isHeaderNormal) {
        this.elem.classList.add(this.CLASS_NAMES.COLLAPSED);
        this.elem.classList.remove(this.CLASS_NAMES.ANIMATE, this.CLASS_NAMES.EXPANDED, this.CLASS_NAMES.NORMAL);
      } else {
        this.elem.classList.add(this.CLASS_NAMES.ANIMATE, this.CLASS_NAMES.COLLAPSED);
        this.elem.classList.remove(this.CLASS_NAMES.EXPANDED, this.CLASS_NAMES.NORMAL);
      }
      setCssVar(this.elem, "--primary-nav-top-buffer", `${this.utilityWrapHeight * -1}px`);
      if (this.FLAGS.IS_DESKTOP_UTILITY_DROPDOWN_OPEN) {
        this.closeActiveDesktopUtilityDropdown(true);
      }
    }

    // Expand header on scroll up
    if (isScrollingUp) {
      // setCssVar(null, '--header-height-dynamic', `${this.elem.offsetHeight}px`);
      this.APP.header.height = this.elem.offsetHeight;
      if (isScrollPosAtTop) {
        this.elem.classList.remove(this.CLASS_NAMES.ANIMATE, this.CLASS_NAMES.EXPANDED, this.CLASS_NAMES.COLLAPSED);
        this.elem.classList.add(this.CLASS_NAMES.NORMAL);
        this.APP.header.state = 'normal';
      } else if (isScrollPosAfterNavBar && Math.abs(this.previousScrollTop - scrollPos) <= this.scrollBuffer) {
        this.elem.classList.add(this.CLASS_NAMES.ANIMATE, this.CLASS_NAMES.EXPANDED);
        this.elem.classList.remove(this.CLASS_NAMES.COLLAPSED, this.CLASS_NAMES.NORMAL);
        this.APP.header.state = 'expanded';
      } else if (!isHeaderNormal && Math.abs(this.previousScrollTop - scrollPos) <= this.scrollBuffer) {
        this.elem.classList.add(this.CLASS_NAMES.ANIMATE, this.CLASS_NAMES.EXPANDED);
        this.elem.classList.remove(this.CLASS_NAMES.COLLAPSED, this.CLASS_NAMES.NORMAL);
        this.APP.header.state = 'expanded';
      }
      setCssVar(this.elem, "--primary-nav-top-buffer", "0px");
    }
    this.previousScrollTop = scrollPos;
  }

  /**
   * Initializes search open/close button events.
   */
  search() {
    if (!this.searchButton || !this.searchCloseButton) return;
    this.searchButton.addEventListener("click", () => {
      this.searchButton.classList.add("header__search-button--search-is-open");
      this.primaryNav.classList.add("header__primary-nav--search-is-open");
      this.searchWrap.classList.add("header__search-wrap--search-is-open");
      this.logo.classList.add("header__logo-link--search-is-open");
      this.searchWrap.querySelector("input").focus();
    });
    this.searchCloseButton.addEventListener("click", () => {
      this.searchButton.classList.remove("header__search-button--search-is-open");
      this.primaryNav.classList.remove("header__primary-nav--search-is-open");
      this.searchWrap.classList.remove("header__search-wrap--search-is-open");
      this.logo.classList.remove("header__logo-link--search-is-open");
    });
  }

  /**
   * Sets up IntersectionObserver to update header skin based on section.
   */
  updateSkin() {
    const topMargin = this.elem.offsetHeight.toFixed(0);
    const bottomMargin = (window.innerHeight - topMargin).toFixed(0);
    const options = {
      rootMargin: `-${topMargin}px 0px -${bottomMargin}px 0px`,
      threshold: [0],
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.target.dataset.navSkin || this.elem.dataset.skin === entry.target.dataset.navSkin) return;
        if (entry.isIntersecting) {
          this.elem.dataset.skin = entry.target.dataset.navSkin;
        }
      });
    }, options);
    this.sections.forEach((section, i) => {
      observer.observe(section);
      if (i === 0) this.elem.dataset.skin = section.dataset?.navSkin;
    });
  }

  /**
   * Sets initial skin for each section based on background brightness.
   */
  setInitialSkin() {
    this.sections.forEach((section) => {
      if (section.dataset?.navSkin) return;
      const computedStyle = window.getComputedStyle(section);
      const bgColor = computedStyle.backgroundColor;
      const rgb = bgColor.match(/\d+/g);
      const brightness = Math.floor(
        (parseInt(rgb[0]) * 299 + parseInt(rgb[1]) * 587 + parseInt(rgb[2]) * 114) / 1000
      );
      section.dataset.navSkin = brightness > 200 ? "dark" : "light";
    });
    this.updateSkin();
  }

  /**
   * Initializes ARIA attributes for nested toggle lists.
   */
  initToggleLists() {
    const toggleItems = this.elem.querySelectorAll("[data-nested-list]");
    toggleItems.forEach((item) => {
      const button = item.querySelector(`.${this.CLASS_NAMES.PARENT_BUTTON}`);
      const menu = item.querySelector(`.${this.CLASS_NAMES.NESTED_MENU}`);
      if (!button || !menu) return;
      const buttonText = this._slugify(button.textContent);
      button.setAttribute("aria-controls", `menu-${buttonText}`);
      menu.setAttribute("id", `menu-${buttonText}`);
    });
  }

  /**
   * Slugifies a string for use in IDs.
   * @param {string} text
   * @returns {string}
   */
  _slugify(text) {
    return text.toLowerCase().replace(/ /g, "-");
  }

  /**
   * Handles back button click in mobile modal navigation.
   * @param {HTMLElement} button
   */
  handleModalBackButtonClick(button) {
    const nestedMenu = button.closest(`.${this.CLASS_NAMES.NESTED_MENU}`);
    if (!nestedMenu) return;
    const parentButton = nestedMenu.previousElementSibling;
    nestedMenu.classList.remove(this.CLASS_NAMES.EXPANDED_SUB_MENU);
    button.setAttribute("aria-expanded", "false");
    parentButton.setAttribute("aria-expanded", "false");
    this.subCategoryReset();
  }

  /**
   * Sets up event listeners for device-specific navigation (mobile/desktop).
   */
  deviceEventController() {
    const isSmMobile = window.matchMedia('(max-width: 768px)').matches;
    const isMobile = window.matchMedia('(max-width: 1023px)').matches;
    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;

    if (isMobile && !this.matchedMedia.isMobile) {
      this.matchedMedia.isMobile = true;
      this.headerHeight = this.elem.offsetHeight;
      setCssVar(null, '--header-height', `${this.headerHeight}px`);

      // Mobile setup
      const modalBackButtons = this.ELEMENTS.MOBILE_MODAL.querySelectorAll(`.${this.CLASS_NAMES.NESTED_MENU_BACK}`);
      modalBackButtons.forEach((button) => {
        button.addEventListener("click", () => this.handleModalBackButtonClick(button));
      });
      this.ELEMENTS.MOBILE_BUTTON_CLOSE_NAV.addEventListener("click", this.handleModalClose.bind(this));
      this.ELEMENTS.MOBILE_BUTTON_OPEN_NAV.addEventListener("click", this.handleModalOpen.bind(this));
      const modalParentButtons = this.ELEMENTS.MOBILE_MODAL.querySelectorAll(`.${this.CLASS_NAMES.PARENT_BUTTON}`);
      modalParentButtons.forEach((button) => {
        button.addEventListener("click", this.handleModalParentButtonClick.bind(this, button));
      });
    }
    if (isDesktop && !this.matchedMedia.isDesktop) {
      this.matchedMedia.isDesktop = true;
      this.headerHeight = this.elem.offsetHeight;
      setCssVar(null, '--header-height', `${this.headerHeight}px`);
      // Desktop setup
      this.ELEMENTS.DESKTOP_UTILITY_PARENT_BUTTONS.forEach((button) => {
        button.removeEventListener("click", (e) => this.handleDesktopUtilityParentButtonClick());
        button.addEventListener("click", (e) => this.handleDesktopUtilityParentButtonClick(button, e));
      });
      this.ELEMENTS.DESKTOP_PRIMARY_PARENT_BUTTONS.forEach((button) => {
        button.removeEventListener("click", (e) => this.handleDesktopPrimaryParentButtonClick());
        button.addEventListener("click", (e) => this.handleDesktopPrimaryParentButtonClick(button, e));
      });

      this.ELEMENTS.DESKTOP_PRIMARY_WRAP.removeEventListener("transitionend", this._onPrimaryTransitionEnd.bind(this));
      this.ELEMENTS.DESKTOP_PRIMARY_WRAP.addEventListener("transitionend", this._onPrimaryTransitionEnd.bind(this));
    }
    if (isSmMobile && this.mobileRightContentWrap) {
      this.mobileRightContentWrap.forEach((element) => element.classList.add('d-none'));
    }
  }

  /**
   * Closes the mobile modal navigation.
   */
  handleModalClose() {
    this.ELEMENTS.MOBILE_MODAL.classList.remove("enabled");
    document.body.classList.remove("lock");
    this.ELEMENTS.MOBILE_BUTTON_OPEN_NAV.focus();
    this.FLAGS.IS_MODAL_OPEN = false;
  }

  /**
   * Opens the mobile modal navigation.
   */
  handleModalOpen() {
    this.ELEMENTS.MOBILE_MODAL.classList.add("enabled");
    document.body.classList.add("lock");
    this.ELEMENTS.MOBILE_BUTTON_CLOSE_NAV.focus();
    this.FLAGS.IS_MODAL_OPEN = true;
  }

  /**
   * Handles parent button click in mobile modal navigation.
   * @param {HTMLElement} button
   */
  handleModalParentButtonClick(button) {
    const parentElement = button.parentElement;
    const menu = parentElement.querySelector(`.${this.CLASS_NAMES.NESTED_MENU}`);
    if (!menu) return;
    const toggleItems = this.ELEMENTS.MOBILE_MODAL.querySelectorAll(`.${this.CLASS_NAMES.NESTED_MENU}`);
    const isCurrentlyCollapsed = menu.classList.contains(this.CLASS_NAMES.EXPANDED_SUB_MENU);
    // Close all lists
    toggleItems.forEach((otherItem) => {
      const otherButton = otherItem.querySelector(`.${this.CLASS_NAMES.PARENT_BUTTON}`);
      const otherMenu = otherItem.querySelector(`.${this.CLASS_NAMES.NESTED_MENU}`);
      if (!otherButton || !otherMenu) return;
      otherMenu.classList.remove(this.CLASS_NAMES.EXPANDED_SUB_MENU);
      otherButton.setAttribute("aria-expanded", "false");
    });
    if (!isCurrentlyCollapsed) {
      menu.classList.add(this.CLASS_NAMES.EXPANDED_SUB_MENU);
      button.setAttribute("aria-expanded", "true");
    }
  }

  /**
   * Closes the active desktop utility dropdown.
   * @param {boolean} resetHeight - Whether to reset the dropdown height CSS var
   */
  closeActiveDesktopUtilityDropdown(resetHeight = false) {
    if (!this.ELEMENTS.DESKTOP_ACTIVE_UTILITY_DROPDOWN) return;

    // Find the parent button and set its aria-expanded to false
    const parentButton = this.ELEMENTS.DESKTOP_ACTIVE_UTILITY_DROPDOWN.parentElement.querySelector('.js-header-utility-parent-button');
    if (parentButton) {
      parentButton.setAttribute("aria-expanded", "false");
    }

    this.ELEMENTS.DESKTOP_ACTIVE_UTILITY_DROPDOWN.classList.add(this.CLASS_NAMES.DESKTOP_COLLAPSED_UTILITY_DROPDOWN);
    this.ELEMENTS.DESKTOP_ACTIVE_UTILITY_DROPDOWN.setAttribute("aria-expanded", "false");
    this.ELEMENTS.DESKTOP_ACTIVE_UTILITY_DROPDOWN = null;
    this.FLAGS.IS_DESKTOP_UTILITY_DROPDOWN_OPEN = false;
    if (resetHeight) {
      setCssVar(this.ELEMENTS.DESKTOP_UTILITY_LINKS, "--utility-dropdown-height", "0px");
    }
    this._removeDesktopUtilityOutsideClickListener();
  }

  /**
   * Handles click on desktop utility parent button to toggle dropdown.
   * @param {HTMLElement} button
   * @param {Event} event
   */
  handleDesktopUtilityParentButtonClick(button, event) {
    event.preventDefault();
    event.stopPropagation();
    const parentElement = button.parentElement;
    const dropdown = parentElement.querySelector(`.${this.CLASS_NAMES.DESKTOP_UTILITY_DROPDOWN}`);
    if (!dropdown) return;
    const dropdownHeight = dropdown.offsetHeight;
    // Toggle logic for utility dropdowns
    if (this.ELEMENTS.DESKTOP_ACTIVE_UTILITY_DROPDOWN === dropdown) {
      this.closeActiveDesktopUtilityDropdown(true);
      this._removeDesktopUtilityOutsideClickListener();
      return;
    } else if (this.ELEMENTS.DESKTOP_ACTIVE_UTILITY_DROPDOWN) {
      this.closeActiveDesktopUtilityDropdown(false);
      this.ELEMENTS.DESKTOP_ACTIVE_UTILITY_DROPDOWN = dropdown;
      dropdown.classList.remove(this.CLASS_NAMES.DESKTOP_COLLAPSED_UTILITY_DROPDOWN);
      setCssVar(this.ELEMENTS.DESKTOP_UTILITY_LINKS, "--utility-dropdown-height", `${dropdownHeight}px`);
      button.setAttribute("aria-expanded", "true");
      dropdown.setAttribute("aria-expanded", "true");
      this._addDesktopUtilityOutsideClickListener(button, dropdown);
    } else {
      this.ELEMENTS.DESKTOP_ACTIVE_UTILITY_DROPDOWN = dropdown;
      dropdown.classList.remove(this.CLASS_NAMES.DESKTOP_COLLAPSED_UTILITY_DROPDOWN);
      setCssVar(this.ELEMENTS.DESKTOP_UTILITY_LINKS, "--utility-dropdown-height", `${dropdownHeight}px`);
      button.setAttribute("aria-expanded", "true");
      dropdown.setAttribute("aria-expanded", "true");
      this._addDesktopUtilityOutsideClickListener(button, dropdown);
    }
    this.FLAGS.IS_DESKTOP_UTILITY_DROPDOWN_OPEN = !this.FLAGS.IS_DESKTOP_UTILITY_DROPDOWN_OPEN;
  }

  /**
   * Adds a document click event listener to close the desktop utility dropdown if clicking outside.
   * @param {HTMLElement} button - The parent button for the open dropdown
   * @param {HTMLElement} dropdown - The open dropdown element
   */
  _addDesktopUtilityOutsideClickListener(button, dropdown) {
    this._removeDesktopUtilityOutsideClickListener(); // Remove any previous
    this._desktopUtilityOutsideClickHandler = (event) => {
      // Only ignore clicks on other utility parent buttons (button only)
      const isUtilityButton = event.target.closest('button.js-header-utility-parent-button');
      if (isUtilityButton) return;
      // If click is inside the dropdown, do nothing (allow links to work)
      if (dropdown.contains(event.target)) return;
      if (!button.contains(event.target) && !dropdown.contains(event.target)) {
        this.closeActiveDesktopUtilityDropdown(true);
        this._removeDesktopUtilityOutsideClickListener();
      }
    };
    document.addEventListener('mousedown', this._desktopUtilityOutsideClickHandler);
  }

  /**
   * Removes the document click event listener for outside clicks on desktop utility dropdown.
   */
  _removeDesktopUtilityOutsideClickListener() {
    if (this._desktopUtilityOutsideClickHandler) {
      document.removeEventListener('mousedown', this._desktopUtilityOutsideClickHandler);
      this._desktopUtilityOutsideClickHandler = null;
    }
  }

  /**
   * Closes the active desktop primary dropdown.
   * @param {boolean} resetHeight - Whether to reset the dropdown height CSS var
   */
  closeActiveDesktopPrimaryDropdown(resetHeight = false) {
    if (!this.ELEMENTS.DESKTOP_ACTIVE_PRIMARY_DROPDOWN || !this.ELEMENTS.DESKTOP_ACTIVE_PRIMARY_BUTTON) return;
    this.ELEMENTS.DESKTOP_ACTIVE_PRIMARY_DROPDOWN.classList.remove(this.CLASS_NAMES.DESKTOP_EXPANDED_PRIMARY_DROPDOWN);
    this.ELEMENTS.DESKTOP_ACTIVE_PRIMARY_BUTTON.classList.remove(this.CLASS_NAMES.DESKTOP_ACTIVE_PRIMARY_BUTTON);
    this.ELEMENTS.DESKTOP_ACTIVE_PRIMARY_DROPDOWN.setAttribute("aria-expanded", "false");
    this.ELEMENTS.DESKTOP_ACTIVE_PRIMARY_DROPDOWN = null;
    this.ELEMENTS.DESKTOP_ACTIVE_PRIMARY_BUTTON = null;
    this.FLAGS.IS_DESKTOP_PRIMARY_DROPDOWN_OPEN = false;
    if (resetHeight) {
      setCssVar(this.elem, "--dropdown-height", "0px");
    }
    this._removeDesktopPrimaryOutsideClickListener();
  }

  /**
   * Handles click on desktop primary parent button to toggle dropdown.
   * @param {HTMLElement} button
   * @param {Event} event
   */
  handleDesktopPrimaryParentButtonClick(button, event) {
    event.preventDefault();
    event.stopPropagation();
    const parentElement = button.parentElement;
    const dropdown = parentElement.querySelector(`.${this.CLASS_NAMES.DESKTOP_PRIMARY_DROPDOWN}`);
    const dropdownLeftContentLinks = (dropdown?.querySelectorAll('div:has(+ .right-content-wrap) a').length) ? dropdown?.querySelectorAll('div:has(+ .right-content-wrap) a') : dropdown?.querySelectorAll('a');
    const menuOnlyLinks = dropdown?.querySelectorAll('.bare-list a');
    if (!dropdown) return;
    const nestedGrid = parentElement.querySelector(`.${this.CLASS_NAMES.DESKTOP_PRIMARY_NESTED_GRID}`);
    const nestedGridHeight = nestedGrid.offsetHeight;
    this.subCategoryReset();

    // Toggle logic for primary dropdowns
    if (this.ELEMENTS.DESKTOP_ACTIVE_PRIMARY_DROPDOWN === dropdown) {
      this.closeActiveDesktopPrimaryDropdown();
      button.classList.remove(this.CLASS_NAMES.DESKTOP_ACTIVE_PRIMARY_BUTTON);
      this.ELEMENTS.DESKTOP_ACTIVE_PRIMARY_BUTTON = null;
      this._removeDesktopPrimaryOutsideClickListener();
      dropdownLeftContentLinks.forEach((link) => link.setAttribute('tabindex', '-1'));
      menuOnlyLinks?.forEach((link) => link.setAttribute('tabindex', '-1'));
      return;
    } else if (this.ELEMENTS.DESKTOP_ACTIVE_PRIMARY_DROPDOWN) {
      this.closeActiveDesktopPrimaryDropdown();
      button.classList.add(this.CLASS_NAMES.DESKTOP_ACTIVE_PRIMARY_BUTTON);
      this.ELEMENTS.DESKTOP_ACTIVE_PRIMARY_DROPDOWN = dropdown;
      dropdown.classList.add(this.CLASS_NAMES.DESKTOP_EXPANDED_PRIMARY_DROPDOWN);
      setCssVar(this.elem, "--dropdown-height", `${nestedGridHeight + this.dropdownHeightBuffer}px`);
      button.setAttribute("aria-expanded", "true");
      dropdownLeftContentLinks.forEach((link) => link.setAttribute('tabindex', '0'));
      menuOnlyLinks?.forEach((link) => link.setAttribute('tabindex', '0'));
      this.ELEMENTS.DESKTOP_ACTIVE_PRIMARY_BUTTON = button;
      this._addDesktopPrimaryOutsideClickListener(button, dropdown);
      dropdownLeftContentLinks[0]?.addEventListener('keydown', (e) => {
        if (e.key === 'Tab' && e.shiftKey) {
          e.preventDefault();
          button.focus();
        }
      });
      dropdownLeftContentLinks[dropdownLeftContentLinks.length - 1]?.addEventListener('keydown', (e) => {
        if (e.key === 'Tab' && !e.shiftKey) {
          e.preventDefault();
          button.focus();
          console.log('should go back to button');
        }
      });
    } else {
      button.classList.add(this.CLASS_NAMES.DESKTOP_ACTIVE_PRIMARY_BUTTON);
      this.ELEMENTS.DESKTOP_ACTIVE_PRIMARY_DROPDOWN = dropdown;
      dropdown.classList.add(this.CLASS_NAMES.DESKTOP_EXPANDED_PRIMARY_DROPDOWN);
      setCssVar(this.elem, "--dropdown-height", `${nestedGridHeight + this.dropdownHeightBuffer}px`);
      button.setAttribute("aria-expanded", "true");
      dropdownLeftContentLinks.forEach((link) => link.setAttribute('tabindex', '0'));
      menuOnlyLinks?.forEach((link) => link.setAttribute('tabindex', '0'));
      this.ELEMENTS.DESKTOP_ACTIVE_PRIMARY_BUTTON = button;
      this._addDesktopPrimaryOutsideClickListener(button, dropdown);
      dropdownLeftContentLinks[0]?.addEventListener('keydown', (e) => {
        if (e.key === 'Tab' && e.shiftKey) {
          e.preventDefault();
          button.focus();
        }
      });
      dropdownLeftContentLinks[dropdownLeftContentLinks.length - 1]?.addEventListener('keydown', (e) => {
        if (e.key === 'Tab' && !e.shiftKey) {
          e.preventDefault();
          button.focus();
        }
      });

      console.log(dropdownLeftContentLinks);
    }
  }

  /**
   * Adds a document click event listener to close the desktop primary dropdown if clicking outside.
   * @param {HTMLElement} button - The parent button for the open dropdown
   * @param {HTMLElement} dropdown - The open dropdown element
   */
  _addDesktopPrimaryOutsideClickListener(button, dropdown) {
    this._removeDesktopPrimaryOutsideClickListener(); // Remove any previous
    this._desktopPrimaryOutsideClickHandler = (event) => {
      // Only ignore clicks on other primary parent buttons (button only)
      const isPrimaryButton = event.target.closest('button.js-header-primary-parent-button');
      if (isPrimaryButton) return;
      // If click is inside the dropdown, do nothing (allow links to work)
      if (dropdown.contains(event.target)) return;
      if (!button.contains(event.target) && !dropdown.contains(event.target)) {
        this.closeActiveDesktopPrimaryDropdown();
        this._removeDesktopPrimaryOutsideClickListener();
      }
    };
    document.addEventListener('mousedown', this._desktopPrimaryOutsideClickHandler);
  }

  /**
   * Removes the document click event listener for outside clicks on desktop primary dropdown.
   */
  _removeDesktopPrimaryOutsideClickListener() {
    if (this._desktopPrimaryOutsideClickHandler) {
      document.removeEventListener('mousedown', this._desktopPrimaryOutsideClickHandler);
      this._desktopPrimaryOutsideClickHandler = null;
    }
  }

  _onPrimaryTransitionEnd() {
    if (this.elem.classList.contains(this.CLASS_NAMES.COLLAPSED)) {
      setCssVar(null, '--header-height-dynamic', `${this.ELEMENTS.DESKTOP_PRIMARY_WRAP.offsetHeight}px`);
    } else {
      // Expanded or normal
      this.expandedHeaderHeight = this.elem.offsetHeight;
      setCssVar(null, '--header-height-dynamic', `${this.elem.offsetHeight}px`);
    }
  }

  /**
  * START HEADER NAV V2 STUFF 
  *
  */
  subCategorySetup() {
    const isSmMobile = window.matchMedia('(max-width: 768px)').matches;
    // const isMobile = window.matchMedia('(max-width: 1023px)').matches;
    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    let eventApplied = false;
    if ((!isSmMobile) && !eventApplied) {
      this.subCategoryButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const sectionKey = button.parentElement.getAttribute('data-section-key');

          const sectionRightContents = (isDesktop)
            ? this.elem.querySelectorAll('.header__primary-dropdown--is-expanded .section-right-content')
            : this.elem.querySelectorAll('.header__modal-nested-menu--is-shown .section-right-content');
          const defaultRightContent = (isDesktop)
            ? this.elem.querySelectorAll('.header__primary-dropdown--is-expanded .default-right-content')
            : this.elem.querySelectorAll('.header__modal-nested-menu--is-shown .default-right-content');

          this.elem.querySelectorAll('.header__primary-nested-item').forEach((elem) => {
            elem.classList.remove('active');
          });

          defaultRightContent.forEach((elem) => {
            elem.classList.add('hidden');
          });

          sectionRightContents.forEach((elem) => {
            elem.classList.add('hidden');
          });

          button.parentElement.classList.toggle('active');

          sectionRightContents.forEach((content) => {
            if (content.getAttribute('data-section-key') === `${sectionKey}`) {
              const sectionRightcontentsLinks = content.querySelectorAll('a');
              const firstLink = sectionRightcontentsLinks[0];
              const lastLink = sectionRightcontentsLinks[sectionRightcontentsLinks.length - 1];

              sectionRightcontentsLinks.forEach((link) => {
                link.setAttribute('tabindex', '0');
              });

              setCssVar(this.elem, "--dropdown-height", `${content.offsetHeight + this.dropdownHeightBuffer}px`);
              this.subCategoryHeightAdjust(content.offsetHeight);
              content.classList.add('active');
              content.classList.remove('hidden');
              firstLink?.focus();
              firstLink?.addEventListener('keydown', (e) => {
                if (e.key === 'Tab' && e.shiftKey) {
                  e.preventDefault();
                  sectionRightcontentsLinks.forEach((link) => {
                    link.setAttribute('tabindex', '-1');
                  });
                  button.focus();
                }
              });
              lastLink?.addEventListener('keydown', (e) => {
                if (e.key === 'Tab' && !e.shiftKey) {
                  e.preventDefault();
                  sectionRightcontentsLinks.forEach((link) => {
                    link.setAttribute('tabindex', '-1');
                  });
                  button.focus();
                }
              });
            } else {
              content.classList.remove('active');
              content.classList.add('hidden');
            }
          });
        });
      });
      eventApplied = true;
    } else if (isSmMobile && !eventApplied) {
      this.mobileNav.classList.add('sm-mobile');
      this.subCategoryButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const sectionKey = button.parentElement.getAttribute('data-section-key');
          const accordionDropdowns = this.elem.querySelectorAll('.header__primary-nested-item .accordion-content-wrap');

          this.elem.querySelectorAll('.header__primary-nested-item').forEach((elem) => {
            if (button.parentElement !== elem) {
              elem.classList.remove('active');
            }
          });

          button.parentElement.classList.toggle('active');

          accordionDropdowns.forEach((dropdown) => {
            const dropdownSection = dropdown.querySelector('.section-right-content');
            dropdownSection.classList.remove('hidden');
            if (dropdown.getAttribute('data-section-key') === `${sectionKey}`) {
              if (dropdown.classList.contains('active')) {
                dropdownSection.classList.remove('active');
                dropdown.classList.remove('active');
                dropdown.style.height = '0px';
                return;
              }

              dropdownSection.classList.add('active');
              dropdown.classList.add('active');
              dropdown.style.height = `${dropdown.scrollHeight}px`;
            } else {
              dropdownSection.classList.remove('active');
              dropdown.classList.remove('active');
              dropdown.style.height = '0px';
            }
          });
        });
      });
      eventApplied = true;
    }
  }

  handleHrefLinks() {
    this.hrefLinks.forEach((link) => {
      //close dropdown without setting other params. Might error? Seems not to but keep an eye on it.
      link.addEventListener("click", (e) => this.closeActiveDesktopPrimaryDropdown());
    });
  }

  subCategoryHeightAdjust(height) {
    this.rightContentWrap.style.setProperty('height', `${height}px`);
  }

  subCategoryReset() {
    this.sectionRightContents.forEach((content) => {
      content.classList.remove('active');
      content.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
    });
    this.primaryNavListItems.forEach((content) => {
      content.classList.remove('active');
      content.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
    });
    this.defaultRightContent.forEach((content) => {
      content.classList.remove('hidden');
      content.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
    });
    (this.rightContentWrap) ? this.rightContentWrap.style.setProperty('height', `auto`) : null;
  }


  /**
   * Initializes the header: skin, search, toggle lists, scroll, and device events.
   */
  init() {
    this.setInitialSkin();
    this.search();
    this.initToggleLists();
    this.subCategorySetup();
    this.handleHrefLinks();
    document.addEventListener("scroll", this.onScroll.bind(this));
    this.deviceEventController();
    window.addEventListener("resize", this.deviceEventController.bind(this));
  }
}
